import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// types
import { ISearchVolunteersByGroupRequest, ISearchVolunteersRequest, SearchVolunteersRequest } from '@joc/api-gateway';
import { BroadcastEnum } from 'shared/components/BroadcastInputTextPopup/helpers';
// redux
import { selectorGetUserAddress } from 'redux/user-service/selector';
import { selectorGetVolunteersAllRecords } from 'redux/volunteer-service/selector';
// functions
import { API } from 'core/API';
import { parseQueryStringVolunteersSearch } from 'core/functions';
// components
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import ButtonDownload from 'shared/components/Buttons/ButtonDownload';
import BroadcastInputTextPopup from 'shared/components/BroadcastInputTextPopup';
import { SnackbarError, SnackbarSuccess } from 'shared/components/_Snackbars';
// styles
import styles from './Actions.module.scss';

const Actions: FC = () => {
	const location = useLocation();

	const [isShowMessageInputPopup, setIsShowMessageInputPopup] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [isReportGenerationSnackbarOpen, setIsReportGenerationSnackbarOpen] = useState(false);
	const [isExportErrorSnackbarOpen, setIsExportErrorSnackbarOpen] = useState(false);

	const volunteers = useSelector(selectorGetVolunteersAllRecords);
	const userAddress = useSelector(selectorGetUserAddress);

	const searchBody: ISearchVolunteersRequest | ISearchVolunteersByGroupRequest = useMemo(
		() => parseQueryStringVolunteersSearch(location.search, userAddress),
		[location.search]
	);

	const buttonDownloadClickHandler = async () => {
		if (isExporting) return;

		setIsExporting(true);
		try {
			await API.adminExportVolunteersOnEmail(SearchVolunteersRequest.fromJS(searchBody));
			setIsReportGenerationSnackbarOpen(true);
		} catch {
			setIsExportErrorSnackbarOpen(true);
		} finally {
			setIsExporting(false);
		}
	};

	const showMessageInputPopupHandler = () => setIsShowMessageInputPopup(true);
	const closeMessageInputPopupHandler = () => setIsShowMessageInputPopup(false);

	return (
		<>
			<div className={styles.wrapper}>
				<ButtonDownload
					clickHandler={buttonDownloadClickHandler}
					disabled={!volunteers.length || isExporting}
				/>
				<ButtonDefault
					title="Broadcast"
					classList={['primary', 'sm']}
					clickHandler={showMessageInputPopupHandler}
					disabled={!volunteers.length}
				/>
				{isShowMessageInputPopup && (
					<BroadcastInputTextPopup
						broadcastType={BroadcastEnum.Volonteers}
						closePopupHandler={closeMessageInputPopupHandler}
					/>
				)}
			</div>
			<SnackbarError
				open={isExportErrorSnackbarOpen}
				onClose={setIsExportErrorSnackbarOpen}
				message="Something went wrong. Please try again."
				width={320}
			/>
			<SnackbarSuccess
				open={isReportGenerationSnackbarOpen}
				onClose={setIsReportGenerationSnackbarOpen}
				message="Report is being generated. You will receive it by email."
				width={420}
			/>
		</>
	);
};

export default Actions;
